/**
 * UUIDv7 — identifiants générés par le CLIENT.
 *
 * Pourquoi pas un identifiant attribué par le serveur : il imposerait un
 * aller-retour réseau pour créer quoi que ce soit, donc aucune création
 * possible dans un sous-sol de supermarché. C'est rédhibitoire pour une app
 * dont c'est précisément le cas d'usage.
 *
 * Pourquoi la v7 et pas la v4 : elle est préfixée par le temps. Les
 * insertions restent donc en queue du b-tree SQLite au lieu de le
 * fragmenter, et un tri par id est un tri chronologique — ce qui donne
 * gratuitement le bon ordre d'affichage sans colonne supplémentaire.
 *
 * Pas de dépendance `uuid` : c'est quinze lignes, et le paquet ferait
 * grossir un bundle qu'on précache pour un usage hors-ligne.
 */

/*
 * Compteur de désambiguïsation intra-milliseconde.
 *
 * Deux appels dans la même milliseconde produiraient sinon des identifiants
 * dont l'ordre relatif est aléatoire. Ça arrive réellement : cocher vite
 * une liste de courses, ou la génération en lot d'une liste depuis un menu.
 * On loge un compteur dans rand_a (12 bits) et on le remet à zéro à chaque
 * nouvelle milliseconde.
 */
let derniereMs = -1
let compteur = 0

export function uuidv7(): string {
  const ms = Date.now()
  if (ms === derniereMs) {
    // 12 bits : au-delà de 4095 identifiants dans la même milliseconde on
    // laisse simplement déborder. À l'échelle d'un foyer c'est inatteignable.
    compteur = (compteur + 1) & 0xfff
  } else {
    derniereMs = ms
    compteur = 0
  }

  const octets = new Uint8Array(16)
  crypto.getRandomValues(octets)

  // 48 bits d'horodatage, gros-boutiste.
  octets[0] = (ms / 2 ** 40) & 0xff
  octets[1] = (ms / 2 ** 32) & 0xff
  octets[2] = (ms / 2 ** 24) & 0xff
  octets[3] = (ms / 2 ** 16) & 0xff
  octets[4] = (ms / 2 ** 8) & 0xff
  octets[5] = ms & 0xff

  // 4 bits de version (7) + 12 bits de compteur.
  octets[6] = 0x70 | ((compteur >> 8) & 0x0f)
  octets[7] = compteur & 0xff

  // 2 bits de variante (10xx) ; les 62 bits restants sont aléatoires.
  octets[8] = 0x80 | (octets[8]! & 0x3f)

  const hex = Array.from(octets, (o) => o.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** Valide la forme d'un identifiant reçu du client avant de l'écrire. */
export function estUuid(v: unknown): v is string {
  return (
    typeof v === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v)
  )
}
